// apps/web/src/screens/login/login.screen.tsx
//
// Working Cognito login form. The "Sign in with Google" button is ALWAYS
// rendered; the email/password form only when allowUsernamePasswordAuth
// is true (dev pool). When the auth service is still on placeholder
// config, we render a diagnostic instead of either path.

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  type InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';

import { Button } from '@/components/ui/button.tsx';
import { authService } from '@/services/auth.service.ts';

const POST_LOGIN_DEFAULT = '/app';

function resolveNextPath(raw: string | null): string {
  if (!raw) return POST_LOGIN_DEFAULT;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  return POST_LOGIN_DEFAULT;
}

function parseRegion(issuerUrl: string): string {
  const m = /https:\/\/cognito-idp\.([^.]+)\.amazonaws\.com\//.exec(issuerUrl);
  if (!m?.[1]) throw new Error(`Cannot derive region from issuer URL ${issuerUrl}`);
  return m[1];
}

export function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = resolveNextPath(params.get('next'));
  const config = authService.getCognitoConfig();
  const configured = authService.isConfigured();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authService.isAuthenticated()) navigate(nextPath, { replace: true });
  }, [navigate, nextPath]);

  if (authService.isAuthenticated()) {
    return <Navigate to={nextPath} replace />;
  }

  if (!configured) {
    return (
      <main
        role="alert"
        className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-3 px-4 py-10"
      >
        <h1 className="text-xl font-semibold">
          Breeze could not load its runtime configuration.
        </h1>
        <p className="text-sm text-slate-700">
          The SPA expected an <code>env.json</code> at this origin and the fetch failed.
          Check the browser console for the underlying error and confirm the deploy is
          healthy.
        </p>
      </main>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const client = new CognitoIdentityProviderClient({
        region: parseRegion(config.issuerUrl),
      });
      const res: InitiateAuthCommandOutput = await client.send(
        new InitiateAuthCommand({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: config.clientId,
          AuthParameters: { USERNAME: email, PASSWORD: password },
        }),
      );
      const tokens = res.AuthenticationResult;
      if (!tokens?.IdToken || !tokens.AccessToken) {
        throw new Error('Cognito returned no tokens; check USER_PASSWORD_AUTH is enabled.');
      }
      authService.setSession({
        idToken: tokens.IdToken,
        accessToken: tokens.AccessToken,
        refreshToken: tokens.RefreshToken ?? null,
      });
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setPending(false);
    }
  }

  function onGoogleClick(): void {
    setError(null);
    try {
      authService.startGoogleSignIn();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Google sign-in could not start. Use the email + password form for now.',
      );
    }
  }

  return (
    <div className="mx-auto flex min-h-svh max-w-md flex-col justify-center gap-6 px-4 py-10">
      <header className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Breeze
        </span>
        <h1 className="text-2xl font-semibold">Sign in</h1>
      </header>

      {config.allowUsernamePasswordAuth && (
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <label className="grid gap-1 text-sm font-medium" htmlFor="login-email">
            Email
            <input
              autoComplete="username"
              className="min-h-11 rounded-md border border-slate-200 px-3 text-base"
              id="login-email"
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              type="email"
              value={email}
            />
          </label>
          <label className="grid gap-1 text-sm font-medium" htmlFor="login-password">
            Password
            <input
              autoComplete="current-password"
              className="min-h-11 rounded-md border border-slate-200 px-3 text-base"
              id="login-password"
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
              type="password"
              value={password}
            />
          </label>
          <Button disabled={pending} type="submit" variant="primary">
            {pending ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      )}

      {config.allowUsernamePasswordAuth && (
        <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
          <span aria-hidden className="h-px flex-1 bg-slate-200" />
          or
          <span aria-hidden className="h-px flex-1 bg-slate-200" />
        </div>
      )}

      <Button onClick={onGoogleClick} type="button" variant="secondary">
        Sign in with Google
      </Button>

      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
